import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { ExtractedMetadataDTO } from '@src/application/pascos/dto';

/**
 * Hook: useExtractPascoMetadata
 * Triggers AI-based metadata extraction for a raw PASCO document.
 * Used during admin approval workflow when metadata is missing.
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for extracting PASCO metadata
 * 
 * @example
 * ```typescript
 * const { mutate: extractMetadata, isPending, data, error } = useExtractPascoMetadata({
 *     onSuccess: (metadata) => {
 *         console.log('Extracted metadata:', metadata);
 *         // Populate form fields with extracted metadata
 *         setFormData({
 *             courseCode: metadata.courseCode || '',
 *             courseName: metadata.courseName || '',
 *             academicYear: metadata.academicYear || '',
 *             semester: metadata.semester?.toString() || '',
 *             level: metadata.level?.toString() || '',
 *             isInterimAssessment: metadata.isInterimAssessment,
 *             isSupplementary: metadata.isSupplementary,
 *         });
 *     }
 * });
 * 
 * // Usage
 * extractMetadata('pasco-123');
 * ```
 */
export function useExtractPascoMetadata(
    options?: Omit<UseMutationOptions<ExtractedMetadataDTO, Error, string>, 'mutationFn'>
) {
    const pascoService = getPascoService();

    return useMutation({
        ...options,
        mutationFn: async (pascoId: string) => {
            return await pascoService.extractMetadata(pascoId);
        },
    });
}
